<template>
  <main class="action-histories">
    <header class="action-histories__header">
      <h1 class="action-histories__title">История действий</h1>
      <div class="action-histories__unit">
        <div
          class="action-histories__group"
        >
          <input-text-search
            class="action-histories__search"
            v-bind:value="searchQuery"
            v-on:input="onInputSearchQuery"
            placeholder="Введите ключевое слово"
          />
        </div>
      </div>
      <div class="action-histories__unit">
        <div
          class="action-histories__group"
          v-if="hasFetchedOptions"
        >
          <date-range-with-presets
            v-bind:presets="presetsOfDates"
            v-model="selectedDates"
            v-on:input="onInputDates" />
          <multiple-autocomplete-input
            label="Выберите сотрудника"
            multiple
            removal-per-chip
            select-all-text="Все сотрудники"
            set-select-all-option
            small-chips
            v-bind:items="employeeOptions"
            v-bind:value="selectedEmployees"
            v-on:input="onInputEmployees"
          />
          <multiple-autocomplete-input
            label="Выберите действие"
            multiple
            removal-per-chip
            select-all-text="Все действия"
            set-select-all-option
            small-chips
            v-bind:items="actionOptions"
            v-bind:value="selectedActions"
            v-on:input="onInputActions"
          />
        </div>
      </div>
    </header>
    <div
      class="action-histories__body-scroll"
      v-if="isNotEmptyHistory"
    >
      <table
        class="action-histories-table action-histories__table"
      >
        <thead class="action-histories-table__head-line">
          <tr>
            <th
              class="action-histories-table__head-cell"
              v-bind:key="field"
              v-for="{ field, title, sorts } in columns"
            >
              <sort-by-column
                class="action-histories-table__sort"
                v-bind:items="sorts"
                v-bind:label="title"
                v-bind:value="sort"
                v-if="sorts.length"
                v-on:input="onInputSort" />
              <template v-else>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="action-histories-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="(cells, rowIndex) in rows"
          >
            <td
              class="action-histories-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in cells"
            >
              <span>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <strong
      v-else-if="isLoadedHistory"
      class="action-histories__body-empty"
    >
      <template>История действий пуста</template>
    </strong>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import { getLocalISODateStringByDate } from '@/mixins/dateMixin';
import InputTextSearch from '@/components/InputTextSearch.vue';

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.title}`;
    return createElement('label', { attrs: { title: this.headTitle }, class: !this.isNotSelected ? 'selected' : '' }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.label}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * @description История действий.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelEmployeeActionHistories',
  components: {
    InputTextSearch,
    MultipleAutocompleteInput,
    DateRangeWithPresets,
    SortByColumn,
  },
  computed: {
    /**
     * @description Не пустая история.
     * @type {boolean}
     */
    isNotEmptyHistory() {
      return this.rows.length !== 0;
    },
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this.fetchHistory();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      actionOptions: [],
      employeeOptions: [],
      /**
       * @description Опции загружены.
       * @type {Boolean}
       */
      hasFetchedOptions: true,
      periodOptions: [],
      selectedActions: [],
      selectedEmployees: [],
      selectedDates: [],
      isLoadedHistory: false,
      columns: [],
      rows: [],
      sort: 'by-date-desc',
      searchQuery: '',
    };
  },
  methods: {
    debounceFetchHistory: debounce(function _() { this.fetchHistory(); }, 1000),
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$employeeActionHistoryGateway
        .viewOptions()
        .then((result) => {
          result.forEach((
            {
              actions,
              employees,
              periods,
              today_period: period,
            },
          ) => {
            this.employeeOptions = employees;
            this.actionOptions = actions;
            this.periodOptions = periods;
            this.selectedDates = period.map((value) => new Date(value));
          });
        });
    },
    /**
     * @description Получить историю.
     * @returns {Promise}
     */
    fetchHistory() {
      this.isLoadedHistory = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getLocalISODateStringByDate);
      return this
        .$employeeActionHistoryGateway
        .viewHistoryActions(
          dateFrom,
          dateTo,
          this.selectedEmployees,
          this.selectedActions,
          this.sort,
          this.searchQuery,
        )
        .then((result) => {
          result.forEach(({ columns, rows }) => {
            this.columns = columns;
            this.rows = rows;
          });
        })
        .finally(() => { this.isLoadedHistory = true; });
    },
    /**
     * @description Обработать ввод действий.
     * @param {string[]} value
     * @returns {void}
     */
    onInputActions(value) {
      this.selectedActions = value;
      this.debounceFetchHistory();
    },
    /**
     * @description Обработать выбор периода.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.debounceFetchHistory();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать ввод сотрудников.
     * @param {string[]} value
     * @returns {void}
     */
    onInputEmployees(value) {
      this.selectedEmployees = value;
      this.debounceFetchHistory();
    },
    /**
     * @description Обработать ввод сортировки.
     * @param {string} value
     * @returns {void}
     */
    onInputSort(value) {
      this.sort = value;
      this.debounceFetchHistory();
    },
    /**
     * @description Обработать ввод ключевого слова.
     * @param {string} value
     * @returns {void}
     */
    onInputSearchQuery(value) {
      this.searchQuery = value;
      this.debounceFetchHistory();
    },
  },
};
</script>

<style lang="scss">
.action-histories {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__units {
    background: #F2F5F8;
    max-width: 1283px;
  }
  &__unit {
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 20px 0 30px;
    border-top: 1px solid rgba(194, 207, 224, 0.50);
    h2 {
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      font-family: "Lato", sans-serif;
    }
    &:first-child {
      border-top: none;
    }
    &:nth-child(2) {
      gap: 0px;
    }
  }
  &__group {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    column-gap: 40px;
    row-gap: 20px;
  }
  &__search {
    max-width: 400px;
  }
  &__body-scroll {
    flex-grow: 1;
    min-height: 400px;
    margin-inline: 23px 20px;
    margin-block: 38px 20px;
    overflow: auto;
  }
  &__body-empty {
    display: block;
    margin: 23px;
  }
  &-table {
    table-layout: fixed;
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    width: max(100%, 1200px);
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: normal;
      line-height: 18px;
    }
    &__body-cell-link {
      text-decoration: none;
      color: black !important;
      transition: color var(--animate-duration);
      &:hover {
        cursor: pointer;
        color: var(--action-color) !important;
      }
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
    }
    &__head-cell {
      padding: 10px 8px;
      text-align: left;
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
      th {
        position: sticky;
        top: 0;
        width: max(200px, 150px);
        background-color: #ffffff;
        z-index: 1;
        &::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 100%;
          height: 2px;
          background-color: var(--action-color);
          z-index: 2;
        }
      }
      th:first-child {
        width: 20%;
      }
      th:last-child {
        width: 70%;
      }
    }
    &__sort {
      cursor: pointer;
      input {
        display: none;
      }
      &::after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }
}
</style>
